// Analytics Overview: answer-first verdict, then one well holding the report
// (Paper "App v3 · Analytics Overview · LP-aligned", White signal v2)
import { useOutletContext, useParams } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LoadingState } from "@/components/shared/layout";

import SiteTrafficMetrics from "@/components/shared/SiteTrafficMetrics";
import {
  getTrafficSummary,
  formatMoney,
  type TrafficSummary,
} from "@/components/shared/charts/TrafficMetricsCard";
import { formatNumeric } from "@/lib/utils/stringUtils";
import PagesComponent from "@/components/shared/PagesComponent";
import DevicesComponent from "@/components/shared/DevicesComponent";
import LocationComponent from "@/components/shared/LocationComponent";
import AcquisitionComponent from "@/components/shared/AcquisitionComponent";
import RealTimeDashboard from "@/components/dashboard/RealTimeDashboard";
import AnalyticsTabBar from "@/components/dashboard/AnalyticsTabBar";
import DemoSiteBanner from "@/components/shared/DemoSiteBanner";
import { useURLState } from "@/hooks/useURLState";
import { usePreviousPeriodMetrics } from "@/hooks/usePreviousPeriodMetrics";
import { useQuery } from "@tanstack/react-query";
import { getRevenueConfig } from "@/lib/api/api";
import { addFilter } from "@/lib/routing/filter-dsl";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import {
  OverviewLoading,
  OverviewTrackingNotLive,
} from "@/components/shared/overview/OverviewStates";
import TrackingNotInstalledBanner from "@/components/dashboard/hub/TrackingNotInstalledBanner";
import { isTrackingNotLive } from "@/lib/utils/trackingLive";
import { formatRangeDay, formatRangeLabel, loadingAxisTicks } from "@/lib/utils/analyticsRange";

interface DashboardContext {
  dashboardData: any;
  isLoading: boolean;
  period: string;
  filters: any[];
  token?: string;
  propertyId?: string;
  property?: any;
  activeDataSources?: any[];
  showRevenue?: boolean;
  revenueLoading?: boolean;
  refetchData?: () => void;
  isAggregateMode?: boolean;
  enabledProperties?: Array<{ propertyId: string; name: string; domain: string }>;
  workspaceId?: string;
}

interface AnomalyData {
  type: "up" | "down";
  currentTraffic: number;
  averageTraffic: number;
  labels: {
    current: string;
    average: string;
  };
}

/** Map Overview breakdown category → URL filter field */
function categoryToFilterField(category: string): string | null {
  switch (category) {
    case "Page":
    case "Entry Page":
    case "Exit Page":
      return "page";
    case "Channels":
    case "Referral":
      return "referrer";
    case "Source":
      return "utm_source";
    case "Campaign":
      return "utm_campaign";
    case "Medium":
      return "utm_medium";
    case "Browser":
      return "browser";
    case "Device":
      return "device";
    case "OS":
      return "os";
    case "country":
      return "country";
    case "city":
      return "city";
    case "region":
      return "region";
    case "Location":
      return "country";
    default:
      return null;
  }
}


/** "in the last 7 days", "this month" — the period as it reads in a sentence */
function periodPhrase(period: string): string {
  const map: Record<string, string> = {
    "1d": "in the last 24 hours",
    PAST_24_H: "in the last 24 hours",
    TODAY: "today",
    YESTERDAY: "yesterday",
    "7d": "in the last 7 days",
    LAST_7_DAYS: "in the last 7 days",
    "30d": "in the last 30 days",
    LAST_30_DAYS: "in the last 30 days",
    "90d": "in the last 90 days",
    LAST_90_DAYS: "in the last 90 days",
    "12m": "in the last 12 months",
    LAST_12_MONTHS: "in the last 12 months",
    mtd: "this month",
    MONTH_TO_DATE: "this month",
    ytd: "this year",
    YEAR_TO_DATE: "this year",
    ALL_TIME: "to date",
  };
  return map[period] ?? "in this range";
}

function deltaPhrase(delta: number | null): string {
  if (delta == null || !Number.isFinite(delta) || delta === 0) return "";
  return delta > 0 ? `, up ${delta}%` : `, down ${Math.abs(delta)}%`;
}

/**
 * Answer-first headline. Deltas compare against the previous equal-length
 * period (`usePreviousPeriodMetrics`); the subtitle names that window. While
 * the comparison loads or if it fails, no delta and no comparison clause.
 */
function OverviewVerdict({
  summary,
  period,
  scopeLabel,
  comparisonPhrase,
}: {
  summary: TrafficSummary;
  period: string;
  scopeLabel: string;
  comparisonPhrase: string;
}) {
  const visitorsNoun = summary.visitors === 1 ? "unique visitor" : "unique visitors";
  const headline =
    `${formatNumeric(summary.visitors)} ${visitorsNoun} ${periodPhrase(period)}${deltaPhrase(summary.visitorsDelta)}.` +
    (summary.hasRevenue
      ? ` Revenue ${formatMoney(summary.revenue)}${deltaPhrase(summary.revenueDelta)}.`
      : "");

  const start = formatRangeDay(summary.rangeStart, period);
  const end = formatRangeDay(summary.rangeEnd, period);
  const range = start && end && start !== end ? `${start} to ${end}` : start || end;
  const hasDelta = summary.visitorsDelta != null || summary.revenueDelta != null;
  const subtitle = [scopeLabel, range].filter(Boolean).join(", ") +
    (hasDelta ? `. Compared with ${comparisonPhrase}.` : "");

  return (
    <div className="flex flex-col gap-1 py-1">
      <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
        {headline}
      </h1>
      {subtitle && (
        <p className="text-[13px] leading-4 text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

export default function Traffic() {
  const context = useOutletContext<DashboardContext>();
  const params = useParams();
  const {
    dashboardData,
    isLoading,
    period,
    filters = [],
    token,
    propertyId,
    showRevenue = true,
    revenueLoading = false,
    isAggregateMode = false,
    enabledProperties = [],
    property,
  } = context || {};

  const getBasePath = () => {
    if (params.magicId && params.password) {
      return `/s/${params.magicId}/${params.password}`;
    }
    if (params.workspaceId) {
      return `/workspace/${params.workspaceId}`;
    }
    if (params.propertySlugId) {
      return `/p/${params.propertySlugId}`;
    }
    if (params.domain) {
      return `/d/${params.domain}`;
    }
    return "";
  };
  const basePath = getBasePath();

  const { state, updateState } = useURLState();
  const workspaceContext = useWorkspaceSafe();

  // Previous equal-length window for KPI deltas. Runs alongside the layout's
  // fetch and never blocks render; deltas stay hidden until it lands.
  const comparisonPropertyIds = isAggregateMode
    ? enabledProperties.map((p) => p.propertyId).filter(Boolean)
    : propertyId
      ? [propertyId]
      : [];
  // Whether revenue is connected at all, so "no revenue" can read as "connect
  // Stripe" only when it is true. Single site only: a workspace view mixes sites.
  const revenueConfigQuery = useQuery({
    queryKey: ["revenue-config", propertyId],
    queryFn: async () => {
      type Setup = { stripe?: { configured?: boolean }; foxy?: { configured?: boolean } };
      const res = (await getRevenueConfig(propertyId as string, token as string)) as
        | (Setup & { revenueSetup?: Setup })
        | null;
      // Same unwrap as useAttributionData: `revenueSetup`, or the setup at the root
      const setup: Setup | null = res?.revenueSetup ?? res ?? null;
      return setup?.stripe?.configured === true || setup?.foxy?.configured === true;
    },
    enabled: !isAggregateMode && !!propertyId && !!token,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const revenueConfigured = revenueConfigQuery.isSuccess ? revenueConfigQuery.data : undefined;

  const previousPeriod = usePreviousPeriodMetrics({
    propertyIds: comparisonPropertyIds,
    period,
    filters,
    token,
    withSeries: true,
  });

  const getGranularity = (period: string) => {
    if (period === "1d") return "hourly";
    if (period === "12m" || period === "ytd") return "monthly";
    return "daily";
  };

  const getComparisonLabel = (granularity: string) => {
    switch (granularity) {
      case "hourly":
        return { current: "Last Hour", average: "Hourly Average" };
      case "weekly":
        return { current: "Last Week", average: "Weekly Average" };
      case "monthly":
        return { current: "Last Month", average: "Monthly Average" };
      default:
        return { current: "Last 24h", average: "Daily Average" };
    }
  };

  const dashboardMetrics =
    dashboardData?.metrics || dashboardData?.stats?.metrics || {};

  const anomalyData: AnomalyData | null = dashboardMetrics?.trends
    ?.sessions?.[0]?.count
    ? (() => {
        const granularity = getGranularity(period);
        const sessions = dashboardMetrics.trends.sessions;
        const currentTraffic = parseInt(sessions[sessions.length - 1].count);
        const sessionsArray = sessions.map((s: { count: string }) =>
          parseInt(s.count)
        );
        const averageTraffic =
          sessionsArray.reduce((a: number, b: number) => a + b, 0) /
          sessionsArray.length;
        const percentageChange =
          ((currentTraffic - averageTraffic) / averageTraffic) * 100;
        const labels = getComparisonLabel(granularity);

        if (Math.abs(percentageChange) >= 25) {
          return {
            type: percentageChange > 0 ? "up" : "down",
            currentTraffic,
            averageTraffic: Math.round(averageTraffic),
            labels,
          };
        }
        return null;
      })()
    : null;

  const openModal = (_modalData: any) => {
    // Modal functionality handled by parent component
  };

  /** Row click on Overview breakdowns → global URL filter (Plausible-style) */
  const filterByParam = (category: string, filter: string) => {
    const field = categoryToFilterField(category);
    if (!field || !filter) return;
    const value =
      field === "device" ? filter.toLowerCase() : filter;
    updateState({
      filters: addFilter(state.filters, field, ":", value),
    });
  };

  // Filtering is server-side: the layout refetches every endpoint with state.filters.

  if (isLoading) {
    return (
      <div className="w-full">
        <AnalyticsTabBar basePath={basePath} className="mb-6" />
        <OverviewLoading showRevenue={showRevenue} ticks={loadingAxisTicks(period, new Date())} />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <LoadingState message="No dashboard data available" size="md" fullPage />
    );
  }

  const metrics =
    dashboardData?.metrics || dashboardData?.stats?.metrics || null;
  if (!metrics) {
    return (
      <LoadingState
        message="Dashboard data is loading. Metrics will appear here once available."
        size="md"
        fullPage
      />
    );
  }

  const metricsForCards = {
    ...dashboardData,
    metrics: dashboardMetrics,
  };

  const summary = getTrafficSummary(
    metricsForCards.metrics,
    showRevenue,
    previousPeriod.data,
  );
  const scopeLabel = isAggregateMode
    ? `${enabledProperties.length} ${enabledProperties.length === 1 ? "site" : "sites"}`
    : metrics?.domain || property?.domain || "";

  // One site that has never sent a pageview: say so instead of a report of zeros.
  const workspaceProperty = !isAggregateMode && propertyId
    ? workspaceContext?.properties?.find((p) => p.propertyId === propertyId)
    : undefined;
  if (
    workspaceProperty &&
    isTrackingNotLive({
      trackingStatus: workspaceProperty.trackingStatus,
      hasRecentViews: workspaceProperty.subscription?.hasRecentViews,
      periodViews: summary.views,
      periodVisitors: summary.visitors,
    })
  ) {
    const domain = workspaceProperty.domain || scopeLabel;
    return (
      <div className="w-full">
        <AnalyticsTabBar basePath={basePath} className="mb-6" />
        <OverviewTrackingNotLive
          domain={domain}
          rangeLabel={formatRangeLabel(summary.rangeStart, summary.rangeEnd, period) || ""}
          showRevenue={showRevenue}
          ticks={loadingAxisTicks(period, new Date())}
          banner={<TrackingNotInstalledBanner sites={[{ propertyId: workspaceProperty.propertyId, domain }]} />}
        />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="w-full">
        <AnalyticsTabBar basePath={basePath} className="mb-6" />

        <div className="flex flex-col gap-4">
          <OverviewVerdict
            summary={summary}
            period={period}
            scopeLabel={scopeLabel}
            comparisonPhrase={previousPeriod.phrase}
          />

          <DemoSiteBanner propertyId={propertyId} />

          {/* One well, raised cards: the marketing site's depth (White signal v2) */}
          <div className="well flex flex-col gap-2">
            {/* 1. Hero metrics + trend — card chrome owned by TrafficMetricsCard */}
            {dashboardMetrics && (
              <SiteTrafficMetrics
                dashBoardData={metricsForCards}
                period={period}
                showRevenue={showRevenue}
                revenueLoading={revenueLoading}
                basePath={basePath}
                previousTotals={previousPeriod.data}
                previousSeries={previousPeriod.series}
                comparisonPhrase={previousPeriod.phrase}
                previousWindow={previousPeriod.window}
                revenueConfigured={revenueConfigured}
              />
            )}

            {/* 2. Live Activity — visitor feed (globe optional) */}
            {token &&
              (propertyId ||
                (isAggregateMode && enabledProperties.length > 0)) && (
                <RealTimeDashboard
                  propertyId={propertyId}
                  token={token}
                  filters={filters}
                  isAggregateMode={isAggregateMode}
                  variant="overview"
                  propertyIds={
                    isAggregateMode
                      ? enabledProperties
                          .map((p) => p.propertyId)
                          .filter(Boolean)
                          .slice(0, 5)
                      : undefined
                  }
                  anomalyData={
                    anomalyData
                      ? {
                          type: anomalyData.type,
                          percentChange: Math.abs(
                            ((anomalyData.currentTraffic -
                              anomalyData.averageTraffic) /
                              anomalyData.averageTraffic) *
                              100
                          ),
                        }
                      : null
                  }
                />
              )}

            {/* 3. Situation board — denser top-N signals; depth lives on detail tabs */}
            {dashboardMetrics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <PagesComponent
                  dashBoardData={metricsForCards}
                  onOpenModal={openModal}
                  onFilterByParam={filterByParam}
                  overviewMode
                />

                <AcquisitionComponent
                  dashBoardData={metricsForCards}
                  onFilterByParam={filterByParam}
                  overviewMode
                />

                <DevicesComponent
                  dashBoardData={metricsForCards}
                  onOpenModal={openModal}
                  onFilterByParam={filterByParam}
                  overviewMode
                />

                <LocationComponent
                  dashBoardData={metricsForCards}
                  onOpenModal={openModal}
                  onFilterByParam={filterByParam}
                  overviewMode
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
